import { useState, useMemo } from "react";
import { useFetch } from "@/hooks/useFetch";
import api, { apiError } from "@/lib/api";
import { Loading, ErrorState, Empty } from "@/components/DataState";
import { AuthImage } from "@/components/AuthImage";
import { PeriodSelector } from "@/components/PeriodSelector";
import { HolidaysManager } from "@/components/HolidaysManager";
import { fmtDate, fmtDateTime, brl, inRange } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarX, Check, X, Image as ImageIcon, FileText, Ban, CheckCheck } from "lucide-react";

const STATUS = [
  { key: "pending", label: "Pendentes" },
  { key: "approved", label: "Aprovadas" },
  { key: "rejected", label: "Reprovadas" },
  { key: "all", label: "Todas" },
];

const JUST_LABEL = {
  text: "Justificativa por texto",
  image: "Justificativa com foto",
  no_route: "Sem rota no dia",
};

const statusBadge = (s) => {
  if (s === "approved") return "bg-emerald-500/15 text-emerald-300";
  if (s === "rejected") return "bg-rose-500/15 text-rose-300";
  return "bg-amber-500/15 text-amber-300";
};
const statusLabel = (s) => (s === "approved" ? "Aprovada" : s === "rejected" ? "Reprovada" : "Pendente");

function FaltaRow({ falta, onReviewed, onPhoto, selected, onToggleSelect }) {
  const [discount, setDiscount] = useState(
    falta.discount_amount != null ? String(falta.discount_amount) : "",
  );
  const [busy, setBusy] = useState(null);

  const hasJust = falta.justification_type != null;

  const review = async (decision) => {
    setBusy(decision);
    try {
      const amount = discount === "" ? null : Number(discount);
      if (amount != null && (isNaN(amount) || amount < 0)) {
        toast.error("Valor de desconto inválido");
        setBusy(null);
        return;
      }
      await api.post(`/admin/faltas/${falta.id}/review`, {
        decision,
        discount_amount: amount,
      });
      toast.success(decision === "approve" ? "Justificativa aprovada" : "Falta reprovada");
      await onReviewed();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <tr className="hover:bg-slate-800/40 align-top" data-testid={`falta-row-${falta.id}`}>
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(falta.id)}
          data-testid={`falta-select-${falta.id}`}
          className="h-4 w-4 cursor-pointer accent-orange-500"
        />
      </td>
      <td className="px-4 py-3">
        <p className="font-medium text-slate-100">{falta.user_name}</p>
        <p className="text-xs capitalize text-slate-500">
          {falta.role}{falta.vehicle_type ? ` · ${falta.vehicle_type}` : ""}
        </p>
      </td>
      <td className="px-4 py-3 text-slate-300">{fmtDate(falta.date)}</td>
      <td className="px-4 py-3">
        {hasJust ? (
          <div className="max-w-xs">
            <p className="text-xs font-semibold text-slate-300">{JUST_LABEL[falta.justification_type] || falta.justification_type}</p>
            {falta.justification_text && (
              <p className="mt-0.5 text-xs text-slate-400 line-clamp-3">{falta.justification_text}</p>
            )}
            {falta.justification_photo && (
              <button
                onClick={() => onPhoto(falta)}
                data-testid={`falta-photo-${falta.id}`}
                className="mt-1 inline-flex items-center gap-1 text-xs text-sky-300 hover:text-sky-200"
              >
                <ImageIcon className="h-3.5 w-3.5" /> Ver foto
              </button>
            )}
          </div>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            <Ban className="h-3.5 w-3.5" /> Sem justificativa
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${statusBadge(falta.status)}`}>
          {statusLabel(falta.status)}
        </span>
        {falta.reviewed_by_name && (
          <p className="mt-1 text-[11px] text-slate-500">
            por {falta.reviewed_by_name}
            {falta.reviewed_at ? ` · ${fmtDateTime(falta.reviewed_at)}` : ""}
          </p>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate-500">R$</span>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            placeholder="0,00"
            data-testid={`falta-discount-${falta.id}`}
            className="h-8 w-24 border-slate-700 bg-slate-950 text-slate-100"
          />
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-1.5">
          <Button
            size="sm"
            className="h-8 bg-emerald-600 px-2 text-white hover:bg-emerald-700"
            disabled={busy != null}
            onClick={() => review("approve")}
            data-testid={`falta-approve-${falta.id}`}
            title="Aprovar justificativa"
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 border-slate-700 px-2 text-slate-300 hover:border-rose-500/40 hover:text-rose-300"
            disabled={busy != null}
            onClick={() => review("reject")}
            data-testid={`falta-reject-${falta.id}`}
            title="Reprovar"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

export default function Faltas() {
  const [tab, setTab] = useState("faltas");
  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-slate-800">
        {[["faltas", "Faltas"], ["feriados", "Feriados e dias sem expediente"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`faltas-tab-${k}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${tab === k ? "border-orange-500 text-orange-300" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === "faltas" ? <FaltasList /> : <HolidaysManager />}
    </div>
  );
}

function FaltasList() {
  const [status, setStatus] = useState("pending");
  const { data, loading, error, refetch } = useFetch("/admin/faltas");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [photo, setPhoto] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const all = useMemo(() => data?.faltas || [], [data]);
  const rows = useMemo(() => {
    return all
      .filter((f) => (status === "all" ? true : f.status === status))
      .filter((f) => inRange(f.date, start, end))
      .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [all, status, start, end]);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, all: all.length };
    all.forEach((f) => { if (c[f.status] != null) c[f.status] += 1; });
    return c;
  }, [all]);

  const visibleIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const pendingVisible = useMemo(() => rows.filter((r) => r.status === "pending"), [rows]);

  const toggleSelect = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const toggleSelectAll = () =>
    setSelected((s) => {
      if (allVisibleSelected) return new Set();
      return new Set(visibleIds);
    });

  const bulkApprove = async (ids) => {
    if (!ids.length) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await api.post(`/admin/faltas/${id}/review`, { decision: "approve", discount_amount: null });
        ok += 1;
      } catch { fail += 1; }
    }
    if (fail) toast.warning(`${ok} aprovada(s), ${fail} com erro`);
    else toast.success(`${ok} falta(s) aprovada(s)`);
    setSelected(new Set());
    await refetch();
    setBulkBusy(false);
  };

  const approveSelected = () => bulkApprove([...selected]);
  const approveAllPending = () => {
    const ids = pendingVisible.map((r) => r.id);
    if (!ids.length) { toast.info("Nenhuma falta pendente na lista atual"); return; }
    if (window.confirm(`Aprovar todas as ${ids.length} faltas pendentes da lista atual?`)) {
      bulkApprove(ids);
    }
  };

  return (
    <div className="space-y-4" data-testid="faltas">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex overflow-hidden rounded-lg border border-slate-700">
          {STATUS.map((s) => (
            <button
              key={s.key}
              onClick={() => setStatus(s.key)}
              data-testid={`falta-status-${s.key}`}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                status === s.key ? "bg-orange-500 text-white" : "bg-slate-900 text-slate-300 hover:bg-slate-800"
              }`}
            >
              {s.label} <span className="opacity-70">({counts[s.key] ?? 0})</span>
            </button>
          ))}
        </div>
        <div className="ml-auto">
          <PeriodSelector start={start} end={end} setStart={setStart} setEnd={setEnd} testPrefix="faltas-period" />
        </div>
      </div>

      <p className="flex items-center gap-2 text-xs text-slate-500">
        <FileText className="h-3.5 w-3.5" />
        Aprove ou reprove a justificativa. Informe o valor do desconto (R$) manualmente para faltas sem justificativa ou reprovadas — ele aparece na Folha de Pagamentos do colaborador.
      </p>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-800 bg-slate-900 p-3">
        <span className="text-sm text-slate-300" data-testid="faltas-selected-count">
          {selected.size} selecionada(s)
        </span>
        <Button
          size="sm"
          className="h-8 bg-emerald-600 text-white hover:bg-emerald-700"
          disabled={bulkBusy || selected.size === 0}
          onClick={approveSelected}
          data-testid="falta-approve-selected"
        >
          <Check className="h-4 w-4" /> Aprovar selecionadas
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8 border-emerald-700/60 text-emerald-300 hover:bg-emerald-500/10"
          disabled={bulkBusy || pendingVisible.length === 0}
          onClick={approveAllPending}
          data-testid="falta-approve-all"
        >
          <CheckCheck className="h-4 w-4" /> Aprovar todas pendentes ({pendingVisible.length})
        </Button>
      </div>

      {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900">
          <table className="w-full text-sm" data-testid="faltas-table">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-semibold">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    data-testid="falta-select-all"
                    className="h-4 w-4 cursor-pointer accent-orange-500"
                  />
                </th>
                <th className="px-4 py-3 font-semibold">Colaborador</th>
                <th className="px-4 py-3 font-semibold">Data</th>
                <th className="px-4 py-3 font-semibold">Justificativa</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Desconto</th>
                <th className="px-4 py-3 text-right font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.length === 0 && <tr><td colSpan={7}><Empty label="Nenhuma falta encontrada" /></td></tr>}
              {rows.map((f) => (
                <FaltaRow
                  key={f.id}
                  falta={f}
                  onReviewed={refetch}
                  onPhoto={setPhoto}
                  selected={selected.has(f.id)}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {photo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
          onClick={() => setPhoto(null)}
          data-testid="falta-photo-modal"
        >
          <AuthImage path={photo.justification_photo} className="max-h-[80vh] w-auto rounded-lg object-contain" />
        </div>
      )}
    </div>
  );
}
